import {useEffect, useState} from 'react';
import {useLocation, useNavigationType, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '@/store';
import {isHistoryBack} from '@/helpers/navigationHistory';
import {
  setPerfectStoreFormData,
  setPerfectStoreMeta
} from '@/store/slices/perfectStoreSlice';
import {getPerfectStoreDetail} from '@/services/perfectStore';
import {mapPerfectStoreApiToFormData} from '@/pages/PerfectStoreUtils';
import {MOCK_PERFECT_STORE_DETAIL} from '@/utils/constants/perfectStoreConstants';
import {logger} from '@/helpers/logger';

const extractDetailPayload = (response) => {
  const candidate =
    response?.data?.content?.[0] ??
    response?.data?.data ??
    response?.data ??
    response?.content?.[0] ??
    null;

  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }

  // Reject envelope-only objects (e.g. {success: true} without target fields)
  if (
    candidate.id == null &&
    !candidate.name &&
    !candidate.targetType &&
    !candidate.displayContent
  ) {
    return null;
  }

  return candidate;
};

/**
 * Loads Perfect Store detail into Redux. Falls back to mock sample when API fails.
 * Skips fetch when returning from a wizard edit (fromWizard) or a history
 * back onto the same record, so Redux edits are kept.
 */
export const usePerfectStoreDetail = ({useMockFallback = true} = {}) => {
  const {id} = useParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const fromWizard = location?.state?.fromWizard === true;
  const dispatch = useAppDispatch();
  const storeId = useAppSelector((state) => state?.perfectStore?.id);
  const skipDetailFetch =
    !id ||
    fromWizard ||
    (isHistoryBack(navigationType, location.key) &&
      storeId != null &&
      String(storeId) === String(id));
  const [isLoading, setIsLoading] = useState(Boolean(id) && !skipDetailFetch);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (skipDetailFetch) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const hydrateFromDetail = (detail) => {
      if (!detail || typeof detail !== 'object') return false;
      const formData = mapPerfectStoreApiToFormData(detail);
      dispatch(setPerfectStoreFormData(formData));
      dispatch(
        setPerfectStoreMeta({
          id: detail.id ?? id,
          uuid: detail.uuid ?? null,
          status: formData.status ?? detail.status ?? null,
          updatedAt: detail.updatedAt ?? null,
          createdAt: detail.createdAt ?? null
        })
      );
      return true;
    };

    const loadDetail = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getPerfectStoreDetail(id);
        const detail = extractDetailPayload(response);
        if (cancelled) return;
        if (!hydrateFromDetail(detail) && useMockFallback) {
          hydrateFromDetail({...MOCK_PERFECT_STORE_DETAIL, id});
        }
      } catch (err) {
        logger.error('Error loading Perfect Store detail:', err);
        if (!cancelled) {
          setError(err);
          if (useMockFallback) {
            hydrateFromDetail({...MOCK_PERFECT_STORE_DETAIL, id});
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadDetail();

    return () => {
      cancelled = true;
    };
  }, [dispatch, id, useMockFallback, skipDetailFetch]);

  return {id, isLoading, error};
};

export default usePerfectStoreDetail;
